import produce from 'immer';
import type { SocialPlatform } from '../../types/instagramAutomation.types';
import {
    ILoadPostAutomationContextSuccess,
    IListInstagramAutomationPostsSuccess,
    ISocialPostsInitialState,
    types,
} from './types';

const initialState: ISocialPostsInitialState = {
    connectedPlatforms: [],
    automationStatus: null,
    oauthUrl: null,
    postsById: {},
    instagramPostsList: [],
    instagramPostsListTotal: 0,
    instagramPostsListPage: 1,
    instagramPostsListTotalPages: 0,
    loadingConnectedPlatforms: false,
    loadingAutomationStatus: false,
    loadingContext: false,
    loadingOAuthUrl: false,
    exchangingOAuth: false,
    publishing: false,
    publishingPlatform: null,
    loadingInstagramPostsList: false,
    togglingAutomationPostIds: {},
    bindingExistingAutomation: false,
    error: null,
};

function upsertPost(draft: ISocialPostsInitialState, post: { _id: string }) {
    draft.postsById[post._id] = post as ISocialPostsInitialState['postsById'][string];
}

const reducer = (state = initialState, action: { type: string; payload?: unknown }) => {
    const { type, payload } = action;
    state = { ...initialState, ...state };

    switch (type) {
        case types.GET_CONNECTED_PLATFORMS:
            return produce(state, draft => {
                draft.loadingConnectedPlatforms = true;
                draft.error = null;
            });
        case types.GET_CONNECTED_PLATFORMS_SUCCESS:
            return produce(state, draft => {
                draft.loadingConnectedPlatforms = false;
                draft.connectedPlatforms = payload as ISocialPostsInitialState['connectedPlatforms'];
            });
        case types.GET_CONNECTED_PLATFORMS_FAIL:
            return produce(state, draft => {
                draft.loadingConnectedPlatforms = false;
                draft.error = payload as ISocialPostsInitialState['error'];
            });

        case types.GET_INSTAGRAM_AUTOMATION_STATUS:
            return produce(state, draft => {
                draft.loadingAutomationStatus = true;
                draft.error = null;
            });
        case types.GET_INSTAGRAM_AUTOMATION_STATUS_SUCCESS:
            return produce(state, draft => {
                draft.loadingAutomationStatus = false;
                draft.automationStatus = payload as ISocialPostsInitialState['automationStatus'];
            });
        case types.GET_INSTAGRAM_AUTOMATION_STATUS_FAIL:
            return produce(state, draft => {
                draft.loadingAutomationStatus = false;
                draft.error = payload as ISocialPostsInitialState['error'];
            });

        case types.LOAD_POST_AUTOMATION_CONTEXT:
            return produce(state, draft => {
                const silent = (payload as { silent?: boolean } | undefined)?.silent;
                if (!silent) {
                    draft.loadingContext = true;
                }
                draft.error = null;
            });
        case types.LOAD_POST_AUTOMATION_CONTEXT_SUCCESS:
            return produce(state, draft => {
                const { automationStatus, connectedPlatforms } = payload as ILoadPostAutomationContextSuccess;
                draft.loadingContext = false;
                draft.automationStatus = automationStatus;
                draft.connectedPlatforms = connectedPlatforms;
            });
        case types.LOAD_POST_AUTOMATION_CONTEXT_FAIL:
            return produce(state, draft => {
                draft.loadingContext = false;
                draft.error = payload as ISocialPostsInitialState['error'];
            });

        case types.GET_INSTAGRAM_AUTOMATION_OAUTH_URL:
            return produce(state, draft => {
                draft.loadingOAuthUrl = true;
                draft.error = null;
            });
        case types.GET_INSTAGRAM_AUTOMATION_OAUTH_URL_SUCCESS:
            return produce(state, draft => {
                draft.loadingOAuthUrl = false;
                draft.oauthUrl = payload as string;
            });
        case types.GET_INSTAGRAM_AUTOMATION_OAUTH_URL_FAIL:
            return produce(state, draft => {
                draft.loadingOAuthUrl = false;
                draft.error = payload as ISocialPostsInitialState['error'];
            });

        case types.EXCHANGE_META_OAUTH:
            return produce(state, draft => {
                draft.exchangingOAuth = true;
                draft.error = null;
            });
        case types.EXCHANGE_META_OAUTH_SUCCESS:
            return produce(state, draft => {
                draft.exchangingOAuth = false;
            });
        case types.EXCHANGE_META_OAUTH_FAIL:
            return produce(state, draft => {
                draft.exchangingOAuth = false;
                draft.error = payload as ISocialPostsInitialState['error'];
            });

        case types.PUBLISH_SOCIAL_POST_FLOW:
            return produce(state, draft => {
                draft.publishing = true;
                draft.publishingPlatform = (payload as { body?: { platform?: SocialPlatform } })?.body
                    ?.platform as SocialPlatform | null;
                draft.error = null;
            });
        case types.PUBLISH_SOCIAL_POST_FLOW_SUCCESS:
            return produce(state, draft => {
                draft.publishing = false;
                draft.publishingPlatform = null;
                upsertPost(draft, payload as { _id: string });
            });
        case types.PUBLISH_SOCIAL_POST_FLOW_FAIL:
            return produce(state, draft => {
                draft.publishing = false;
                draft.publishingPlatform = null;
                draft.error = payload as ISocialPostsInitialState['error'];
            });

        case types.CREATE_SOCIAL_POST_SUCCESS:
        case types.PUBLISH_SOCIAL_POST_SUCCESS:
        case types.GET_SOCIAL_POST_SUCCESS:
        case types.CONFIGURE_INSTAGRAM_AUTOMATION_SUCCESS:
        case types.PATCH_SOCIAL_POST_SUCCESS:
            return produce(state, draft => {
                upsertPost(draft, payload as { _id: string });
                const post = payload as ISocialPostsInitialState['postsById'][string];
                const listIndex = draft.instagramPostsList.findIndex(p => p._id === post._id);
                if (listIndex > -1) {
                    draft.instagramPostsList[listIndex] = post;
                }
                delete draft.togglingAutomationPostIds[post._id];
            });

        case types.LIST_INSTAGRAM_AUTOMATION_POSTS:
            return produce(state, draft => {
                draft.loadingInstagramPostsList = true;
                draft.error = null;
            });
        case types.LIST_INSTAGRAM_AUTOMATION_POSTS_SUCCESS:
            return produce(state, draft => {
                const { posts, totalCount, currentPage, totalPages } = payload as IListInstagramAutomationPostsSuccess;
                const safePosts = Array.isArray(posts) ? posts : [];
                draft.loadingInstagramPostsList = false;
                draft.instagramPostsList = safePosts;
                draft.instagramPostsListTotal = totalCount ?? safePosts.length;
                draft.instagramPostsListPage = currentPage ?? 1;
                draft.instagramPostsListTotalPages = totalPages ?? 1;
                safePosts.forEach(post => {
                    draft.postsById[post._id] = post;
                });
            });
        case types.LIST_INSTAGRAM_AUTOMATION_POSTS_FAIL:
            return produce(state, draft => {
                draft.loadingInstagramPostsList = false;
                draft.error = payload as ISocialPostsInitialState['error'];
            });

        case types.PATCH_SOCIAL_POST:
            return produce(state, draft => {
                const postId = (payload as { postId: string }).postId;
                draft.togglingAutomationPostIds[postId] = true;
                draft.error = null;
            });
        case types.PATCH_SOCIAL_POST_FAIL:
            return produce(state, draft => {
                const { postId } = payload as { postId: string; error?: unknown };
                if (postId) {
                    delete draft.togglingAutomationPostIds[postId];
                }
                draft.error = (payload as { error?: ISocialPostsInitialState['error'] }).error ?? null;
            });

        case types.BIND_EXISTING_IG_AUTOMATION:
            return produce(state, draft => {
                draft.bindingExistingAutomation = true;
                draft.error = null;
            });
        case types.BIND_EXISTING_IG_AUTOMATION_SUCCESS:
            return produce(state, draft => {
                draft.bindingExistingAutomation = false;
                const post = payload as ISocialPostsInitialState['postsById'][string];
                upsertPost(draft, post);
                const listIndex = draft.instagramPostsList.findIndex(p => p._id === post._id);
                if (listIndex > -1) {
                    draft.instagramPostsList[listIndex] = post;
                } else if (post.instagram_automation) {
                    draft.instagramPostsList = [post, ...draft.instagramPostsList];
                    draft.instagramPostsListTotal += 1;
                }
            });
        case types.BIND_EXISTING_IG_AUTOMATION_FAIL:
            return produce(state, draft => {
                draft.bindingExistingAutomation = false;
                draft.error = payload as ISocialPostsInitialState['error'];
            });

        case types.RESET_SOCIAL_POSTS_ERROR:
            return produce(state, draft => {
                draft.error = null;
            });

        default:
            return state ?? initialState;
    }
};

export { reducer, initialState as socialPostsInitialState };
